/**
 * CollaboratorPanel Component - Manage workshop collaborators
 */
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Users, CheckCircle, Clock } from 'lucide-react';
import ProfilePicture from '@/components/ProfilePicture';

export const CollaboratorPanel = ({
  workshop,
  isCreator,
  canEdit,
  myCollab,
  onInviteCollaborator,
  onApproveFinal,
  onSubmitForApproval
}) => {
  const acceptedCollabs = workshop.collaborators?.filter(c => c.status === 'accepted') || [];
  const allApproved = acceptedCollabs.every(c => c.has_approved_final);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Income Split</h4>
        {isCreator && canEdit && (
          <Button size="sm" onClick={onInviteCollaborator}>
            <Users className="w-4 h-4 mr-1" /> Invite Collaborator
          </Button>
        )}
      </div>
      
      <div className="space-y-3">
        {workshop.collaborators?.map(collab => (
          <CollaboratorCard key={collab.id} collab={collab} creatorId={workshop.creator_id} />
        ))}
      </div>
      
      <RevenueBreakdown workshop={workshop} />
      
      {/* Approval status for collaborative workshops */}
      {workshop.status === 'draft' && workshop.collaborators?.length > 1 && (
        <ApprovalStatus
          collaborators={acceptedCollabs}
          myCollab={myCollab}
          allApproved={allApproved}
          onApproveFinal={onApproveFinal}
          onSubmitForApproval={onSubmitForApproval}
          workshopId={workshop.id}
        />
      )}
    </div>
  );
};

const CollaboratorCard = ({ collab, creatorId }) => (
  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
    <div className="flex items-center gap-3">
      <ProfilePicture user={collab.user} size="sm" />
      <div>
        <p className="font-medium">{collab.user?.full_name || 'Unknown'}</p>
        <p className="text-sm text-gray-400">
          {collab.user_id === creatorId && '(Creator) '}
          {collab.status === 'pending' && '⏳ Pending'}
          {collab.status === 'accepted' && '✓ Accepted'}
          {collab.status === 'rejected' && '✗ Declined'}
        </p>
      </div>
    </div>
    <div className="text-right">
      <p className="text-xl font-bold text-cyan-400">{collab.income_percentage}%</p>
      <p className="text-xs text-gray-500">of practitioner revenue</p>
    </div>
  </div>
);

const RevenueBreakdown = ({ workshop }) => {
  const feePct = workshop.platform_fee_percent ?? 20;
  const platformFee = workshop.platform_fee ?? (workshop.price * feePct / 100);
  return (
  <div className="p-4 bg-slate-800 rounded-lg">
    <h5 className="text-sm font-medium mb-2">Revenue Breakdown (per sale)</h5>
    <div className="space-y-1 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-400">Your price:</span>
        <span>${workshop.price.toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-400">Platform fee ({feePct}%):</span>
        <span>-${platformFee.toFixed(2)}</span>
      </div>
      <div className="flex justify-between font-medium">
        <span className="text-gray-400">Split between collaborators:</span>
        <span>${(workshop.practitioner_net ?? (workshop.price - platformFee)).toFixed(2)}</span>
      </div>
      <div className="flex justify-between pt-2 border-t border-slate-600">
        <span className="text-gray-400">Processing, paid by buyer (3% + $0.30):</span>
        <span>${(workshop.processing_fee ?? (workshop.price * 0.03 + 0.30)).toFixed(2)}</span>
      </div>
      <div className="flex justify-between font-medium">
        <span>User pays:</span>
        <span>${(workshop.total_price ?? 0).toFixed(2)}</span>
      </div>
    </div>
  </div>
  );
};

const ApprovalStatus = ({
  collaborators,
  myCollab,
  allApproved,
  onApproveFinal,
  onSubmitForApproval,
  workshopId
}) => (
  <div className="p-4 bg-cyan-600/10 border border-cyan-500 rounded-lg">
    <p className="text-sm mb-2">All collaborators must approve before submission:</p>
    {collaborators.map(c => (
      <div key={c.id} className="flex items-center gap-2 text-sm">
        {c.has_approved_final ? (
          <CheckCircle className="w-4 h-4 text-green-500" />
        ) : (
          <Clock className="w-4 h-4 text-yellow-500" />
        )}
        <span>{c.user?.full_name || 'Unknown'}</span>
        <span className="text-gray-500">({c.income_percentage}%)</span>
      </div>
    ))}
    
    {myCollab && !myCollab.has_approved_final && (
      <Button onClick={() => onApproveFinal(workshopId)} className="mt-2">
        <CheckCircle className="w-4 h-4 mr-2" /> Approve Workshop
      </Button>
    )}
    
    {allApproved && (
      <Button 
        onClick={() => onSubmitForApproval(workshopId)} 
        className="mt-2 bg-green-600 hover:bg-green-700"
      >
        Submit for Admin Approval
      </Button>
    )}
  </div>
);
